import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { injectIntl } from 'react-intl';
import { HashRouter as Router, Route } from 'react-router-dom';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { i18nIntl, NSchange, preventSelection, getLayout, isNotEmpty, stripTags, getRemotedbUrl, ensureBoolean, getLayoutQuiz } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';
import { answerScore, getMostAnswer, getQuizResult, getQuizScore } from '../../lib/qz';
import { stage_promotion_types as spt } from '../../data/bootstrap_stages.js';

import { CreateButton, MainButton, SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox';
import { DeleteButton } from '../builder/ConfirmButton';
import DesignContainerProps from '../builder/DesignContainerProps';
import DesignWarningMessage from '../builder/DesignWarningMessage';
import DisabledArea from '../builder/DisabledArea.js';
import FloatingLayer from '../builder/FloatingLayer';
import Input from '../builder/Input';
import Message from '../builder/Message';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range';
import Select from '../builder/Select';
import SectionTitle from '../builder/SectionTitle';
import SectionSubtitle from '../builder/SectionSubtitle';
import TextArea from '../builder/TextArea';
import ToggleButton from '../builder/ToggleButton';
import Tooltip from '../builder/Tooltip.js';

import QuizResultFlex from '../users/QuizResultFlex';
import SuccessFlex from '../users/SuccessFlex';

import Question from '../front/quiz/Question';
import QuizStats from '../front/quiz/QuizStats';

import Layout from '../front/Layout';

import QuizClient from '../../containers/QuizClient';

import QuestionOptions from './QuestionOptions';
import QuizResultOptions from './QuizResultOptions';

import CardSelector from '../ui_items/CardSelector.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import AgentCards from '../ui_items/AgentCards.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import ColorPicker from '../builder/ColorPicker.js';
const TextAreaPlaceHolder = injectIntl(transferPlaceholder(TextArea));

const Nav = (props) => {
	const pm = props?.pm ?? false;
	const is_wl = (pm && pm.promo_type != undefined && pm.promo_type == '2') ? true : false;

	const base = (props.basename != undefined) ? `${props.basename}/` : '';

	const in_stages = (base.match(/stages\/?/i) != null) ? true : false;
	const is_multistage = props?.is_multistage ?? false;

	const stage = props?.stage ?? {};

	const stages_with_final_messages = [spt.KNOWLEDGE_QUIZ.id, spt.PERSONALITY_QUIZ.id, spt.PRODUCT_RECOMMENDER.id];
	const with_final_messages = (stage.id == undefined || stages_with_final_messages.includes(parseInt(stage.promotion_type_id))) ? true : false;

	const sections = [];
	if(in_stages) sections.push({ label:"Back to Stages", link:`${base}options`, icon:'knk-arrow-l-big' });
	sections.push({ label:"Questions", link:`${base}questions`, icon:'knk-questions' });
	sections.push({ label:"Design", link:`${base}design`, icon:'knk-artist-mode' });
	if(with_final_messages) sections.push({ label:"Final messages", link:`${base}results`, icon:'knk-goal' });
	if(!in_stages) sections.push({ label:"Container", link:`${base}questions_container`, icon:'knk-page' });
	if(!is_multistage) sections.push({ label:"Statistics", link:`${base}quizstats`, icon:'knk-stats' });
	sections.push({ label:"Test the quiz", link:`${base}testreact`, icon:'knk-ok' });
	if(is_wl && !in_stages) sections.push({ label:"Advanced", link:`${base}quiz_advanced`, icon:'knk-script' });

	return (
		<NavigationTabsBuilder
			active={props.active}
			sections={sections}
			relative_selection={true}
		>{props.stage_indicator}</NavigationTabsBuilder>
	)
};

const CreateNewQuestionLayer = ({ selectType, closeLayer, ...props }) => {
	const i18n = (m) => i18nIntl(m, props.intl);

	const question_types = [{
		type:'single',
		icon:<div class="icon_group">
			<span class="knk-off" />
			<span class="knk-off" />
			<span class="knk-on" />
			<span class="knk-off" />
		</div>,
		title:i18n("Single answer"),
		description:i18n("Multiple choice question, from which the user must choose one."),
		onClick:() => selectType('single'),
		extra_content:<ButtonCollection _class={{other:'gui_flex_center'}}>
			<MainButton>Create</MainButton>
		</ButtonCollection>,
	}, {
		type:'multi',
		icon:<div class="icon_group">
			<span class="knk-on" />
			<span class="knk-off" />
			<span class="knk-on" />
			<span class="knk-on" />
		</div>,
		title:i18n("Multiple-answer"),
		description:i18n("Multiple choice question, from which the user can choose more than one option."),
		onClick:() => selectType('multi'),
		extra_content:<ButtonCollection _class={{other:'gui_flex_center'}}>
			<MainButton>Create</MainButton>
		</ButtonCollection>,
	}, {
		type:'multisort',
		icon:<div class="icon_group">
			<span class="knk-number-1" />
			<span class="knk-number-3" />
			<span class="knk-number-4" />
			<span class="knk-number-2" />
		</div>,
		title:i18n("Sortable answers"),
		description:i18n("Multiple choice question where the user must choose the correct order."),
		onClick:() => selectType('multisort'),
		extra_content:<ButtonCollection _class={{other:'gui_flex_center'}}>
			<MainButton>Create</MainButton>
		</ButtonCollection>,
	}, {
		type:'advanced',
		icon:<div class="icon_group">
			<span class="knk-open-text" />
			<span class="knk-options-list" />
			<span class="knk-button" />
			<span class="knk-rating" />
		</div>,
		title:i18n("Advanced"),
		description:i18n("Use all types of answers available: ratings, open, lists, radio buttons, sortable and closed answers."),
		onClick:() => selectType('advanced'),
		extra_content:<ButtonCollection _class={{other:'gui_flex_center'}}>
			<MainButton>Create</MainButton>
		</ButtonCollection>,
	}];

	return (
		<FloatingLayer
			backButton={closeLayer}
			_class={{ size:'big' }}
		>
			<div class="without_actions">
				<SectionTitle>Select the type of question you want to create</SectionTitle>

				<SectionSubtitle>What do you want to ask your users? Choose one question type among the following options</SectionSubtitle>

				<CardSelector
					items={question_types}
					promotion={props.promotion}
				/>
			</div>
		</FloatingLayer>
	);
};

const DragHandle = SortableHandle(() => <span class="sorter_target" />);

const SortableQuestions = SortableContainer(({ items, intl, actions, current, basename }) => {
	return (
		<ul
			class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap"
			style={{ overflow:'auto', maxHeight:'calc(100dvh - 380px)' }}
		>
			{items.map((value, index) => {
				return (
					<SortableItem
						key={`item-${index}`}
						index={index}
						q={value}
						actions={actions}
						current={current}
						intl={intl}
						basename={basename}
					/>
				)
			})}
		</ul>
	);
});

const SortableItem = SortableElement(({ q, current, actions, basename }) => {
	const base = (basename != undefined) ? `#${basename}/` : '#';

	return (
		<li class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
			<div class="item_header">
				<DragHandle />

				<div class="item_title_container">
					<div>
						<ActionLink
							_href={`${base}questions/${q.Question.id}`}
							disabled={q.Question.status == "0"}
							_class={{ other:'item_title without_icon' }}
						><strong>{q.Question.question_st}</strong></ActionLink>
					</div>
				</div>

				<ButtonCollection>
					<ToggleButton
						squared={true}
						active={((q.Question.status == 1) ? true : false)}
						icon='knk-visible'
						icon_off='knk-invisible redIcon'
						onClick={() => actions.toggleQuestion(q.Question.id)}
						aria-label="Toggle visibility"
					/>

					{(current == q.Question.id) ? (
						<SquaredButton
							tooltip={<Tooltip
								with_icon={false}
							>{(q.Question.status == 1) ? "disable question" : "enable question"}</Tooltip>}
							icon={(q.Question.status == 1) ? 'knk-visible' : 'knk-invisible redIcon'}
							onClick={() => actions.toggleQuestion(q.Question.id)}
							aria-label="Toggle visibility"
						/>
					) : (
						<SquaredButton
							icon='knk-edit'
							_href={`${base}questions/${q.Question.id}`}
							aria-label="Edit"
						/>
					)}

					<SquaredButton
						icon='knk-copy'
						onClick={() => actions.copyQuestion(q.Question)}
						aria-label="Copy"
					/>

					<DeleteButton
						squared={true}
						_position="left"
						onClick={() => actions.deleteQuestion(q.Question)}
						aria-label="Delete"
					/>
				</ButtonCollection>
			</div>
		</li>
	)}
);

const UserTestResumee = ({ user, questions, ...props }) => {
	const i18n = (m) => i18nIntl(m, props.intl);

	if(questions.length == 0) return null;

	const score_type = props?.score_type ?? '0';

	return (
		<ul class="quiz_test_debug">
			{questions.map((question, i) => {
				const qid = question.Question.id;

				let answers = [];
				for(let j in question.Answer) {
					if(user[qid][question.Answer[j].id] == undefined) continue;
					answers.push(question.Answer[j]);
				}

				// if the question is multiorder sort them
				if(question.Question.multiorder) {
					answers = answers.sort((a, b) => {
						return parseInt(user[a.question_id][a.id]) - parseInt(user[b.question_id][b.id]);
					});
				}

				return (
					<li key={qid} class="question_debug">
						<div class="question_title_debug">
							<span class="title cutTextOnOverflow">{i + 1}. {question.Question.question_st}</span>

							<ul class="debug_answers">
								{answers.map((answer, k) => {
									const ua = user[answer.question_id][answer.id];

									let anscore = answerScore(answer, user, question.Question);
									if(score_type) {
										anscore = anscore.toString().split("").join(", ");
									}

									return (
										<li key={k}>
											<span>
												{answer.answer_st}

												{(answer.config.type == 'closed_text') && (
													<span />
												)}

												{(answer.config.type == 'list') && (
													<span>
														<br />
														<span>{ua}</span>
													</span>
												)}

												{(answer.config.type == 'rating') && (
													<span>
														<br />
														<span>{ua} / {answer.config.star_count} {i18n("stars")}</span>
													</span>
												)}

												{(answer.config.type == 'open_text') && (
													<span>
														<br />
														<span class="indented_text">{ua}</span>
													</span>
												)}
											</span>

											<span class="num_points">{anscore}</span>
										</li>
									)
								})}
							</ul>
						</div>
					</li>
				)
			})}
		</ul>
	)
};

const Quiz = (props) => {
	useCSS('comp/quiz.css');
	useCSS('comp/agent_cards.css');

	const i18n = (m) => i18nIntl(m, props.intl);

	const getSeed = () => {
		return Math.random() * 100000;
	};
	const state = props.store.getState();

	const getQuestionsStage = (questions = undefined) => {
		const q = questions ?? state.questions.questions;
		if(props.stage_id == undefined) return q;
		
		return q.filter((q) => (q.Question.stage == props.stage_id)).sort((a, b) => {
			return (parseInt(a.Question.pos) < parseInt(b.Question.pos)) ? -1 : 1;
		});
	};

	const getQuizResultsStage = () => {
		const qr = state_promotion.quiz_results;

		if(props.stage_id == undefined) return qr;
		return qr.filter((q) => (q.QuizResult.stage == props.stage_id));
	};

	const state_promotion = state.questions.pm;

	const [ new_question, setNewQuestion ] = useState(false);
	const [ promotion, setPromotion ] = useState({ ...state_promotion.attributes, layout_props:state_promotion.attributes?.layout_props ?? {} });
	const [ questions, setQuestions ] = useState(getQuestionsStage());
	const [ quiz_results, setQuizResults ] = useState(getQuizResultsStage());
	const [ user, setUser ] = useState(false);
	const [ foo, setFoo ] = useState(getSeed());
	const [ final, setFinal ] = useState(false);

	const base = (props.basename != undefined) ? `#${props.basename}/` : '#';

	const layout_props_model = getLayout(promotion);
	layout_props_model.quiz = getLayoutQuiz(props.stage, layout_props_model);

	useEffect(() => {
		changeCssVariable(layout_props_model);
	}, []);

	// props.store.subscribe(() => {
	// 	const state = props.store.getState();
	// 	setQuestions(getQuestionsStage);
	// })

	const changeCssVariable = (_layout_props_model) => {
		if(window.css_var.setCustomStyles == undefined) return;
		window.css_var.setCustomStyles(_layout_props_model);
	};

	const addNewQuestion = (type, history) => {
		const data = {
			question:i18n("Type your question here..."),
			answers_row:0,
			config:{
				hidecontinue:false,
			},
		};

		if(props.stage_id != undefined) data.stage = props.stage_id;

		switch(type) {
			case 'single':
				data.multianswer = 0;
				data.is_advanced = 0;
				data.question_type = '1';
				break;

			case 'multi':
				data.multianswer = 1;
				data.is_advanced = 0;
				data.min_answers = 1;
				data.question_type = '2';
				break;

			case 'multisort':
				data.multianswer = 1;
				data.multiorder = 1;
				data.is_advanced = 0;
				data.question_type = '3';
				break;

			case 'advanced':
				data.is_advanced = 1;
				break;
		}

		$.post(`/quizs/addquestion/${promotion.id}/`, data, (res) => {
			if(Array.isArray(res.Question.config)) res.Question.config = {};

			props.store.dispatch({ type:'ADDQUESTION', questions:res });

			setQuestions(getQuestionsStage(props.store.getState().questions.questions));
			setNewQuestion(false);

			if(history != undefined) {
				history.push(`questions/${res.Question.id}`);
			}
		}, 'JSON');
	};

	const addNewQr = () => {
		const msg = i18n("Type your message here...");

		const empty_QR = {
			id:'new',
			promotion_id:promotion.id,
			image:null,
			message:msg,
			message_st:msg,
		}
		if(props.stage_id != undefined) empty_QR.stage = props.stage_id;
		
		const _quiz_results = [ ...quiz_results ];
		_quiz_results.push({ QuizResult:empty_QR });
		setQuizResults(_quiz_results);

		gotoFinalMessagesEditor(empty_QR.id);
	};

	const clientAction = (_user) => {
		setUser(_user);
	};

	const findQuestionById = (id) => {
		return questions.find((q) => (q.Question.id == id));
	};

	const toggleQuestion = (id) => {
		const _question = findQuestionById(id);
		_question.Question = { ..._question.Question, status:(_question.Question.status == 1) ? 0 : 1 };

		const _questions = [];
		questions.map((q) => {
			if(q.Question.id == id) {
				_questions.push(_question);
				return;
			}

			_questions.push(q);
		});

		setQuestions(_questions);
	};

	const deleteQuestion = (_question) => {
		props.store.dispatch({
			qid:_question.id,
			type:'DELETEQUESTION',
		});

		setQuestions(questions.filter((q) => (q.Question.id != _question.id)));
		$.post(getRemotedbUrl('/quizs/deleteQuestion'), { promotion_id:_question.promotion_id, id:_question.id });
	};

	const copyQuestion = (_question) => {
		$.post(getRemotedbUrl('/quizs/copyquestion'), { id:_question.id }, (res) => {
			if(!(res instanceof Array)) res = [res];

			props.store.dispatch({
				questions:res,
				type:'ADDQUESTION',
			});

			const _questions = [ ...questions ];
			for(let i in res) {
				_questions.push(res[i]);
			}
			setQuestions(_questions);
		}, 'JSON');
	};

	const copyResult = (result) => {
		$.post(getRemotedbUrl(`/quizs/copyresult/${promotion.id}`), { id:result.id }, (res) => {
			state_promotion.quiz_results = res;
			setQuizResults(res);
		}, 'JSON');
	};

	const deleteResult = (result) => {
		state_promotion.quiz_results = state_promotion.quiz_results.filter((q) => (q.QuizResult.id != result.id));
		setQuizResults(quiz_results.filter((q) => (q.QuizResult.id != result.id)));
		$.post(getRemotedbUrl('/quizs/deleteresult'), { promotion_id:result.promotion_id, id:result.id });
	};

	const onSortEnd = ({ oldIndex, newIndex }) => {
		props.store.dispatch({
			newIndex:newIndex,
			oldIndex:oldIndex,
			stage:props.stage_id,
			type:'SORTQUESTION',
		});

		const state = props.store.getState();
		setQuestions(getQuestionsStage(state.questions.questions));

		// const _questions = arrayMove(questions, oldIndex, newIndex);

		// for(let i in _questions) {
		// 	_questions[i].Question.pos = i;
		// }
		//setQuestions(_questions);

		preventSelection(false);
	};

	const quizResultChange = (k, v, id) => {
		const _quiz_results = [ ...quiz_results ];
		_quiz_results.map((qr) => {
			if(qr.QuizResult.id != id) return;
			qr.QuizResult[k] = v;
		});
		setQuizResults(_quiz_results);
	};

	const changeEntity = (k, v) => {
		if(props.stage_id != undefined) {
			props.changeStage(k, v);
			return;
		}

		change(k, v);
	};

	const saveEntity = (cb) => {
		if(props.stage_id != undefined) {
			props.saveStage(cb);
			return;
		}

		savePromo(cb);
	};

	const change = (k, v) => {
		NSchange(promotion, k, v);
		window.pm.set(k,v);

		// when activating the summary set the defaults
		if(k == 'config.quiz_resume.show' &&  v != "0") {
			if(promotion.config.quiz_resume.detail == undefined) NSchange(promotion, 'config.quiz_resume.detail', "1");
			if(promotion.config.quiz_resume.buttons == undefined) NSchange(promotion, 'config.quiz_resume.buttons', "continue");
		}

		setPromotion({ ...promotion, [k]:v });

		if(k == 'quiz_score_type') {
			if(props.stage_id != undefined) {
				props.changeStage(k, v);
				return;
			}

			props.pm.set(k, v);
			savePromo();
		}
	};

	const savePromo = (cb) => {
		window.pm.save((r) => {
			if(cb != undefined) cb();
		});
	};

	const saveQuiz = (cb) => {
		$.post(`/quizs/save/${promotion.id}`, {
			questions:JSON.stringify(questions),
		}, (res) => {
			if(res.redirect_url != undefined) window.location.href = res.redirect_url;
			if(cb != undefined) cb();
			setQuestions(getQuestionsStage(res));
		}, 'JSON');
	};

	const saveResults = (cb) => {
		const hash_value = window.location.hash.substring(1).split('&').reduce((res, item) => {
			return item.split("=")[0];
		}, {});

		// ens interessen els id=>new perque estem a la fitxa si is_new
		let to_save_qresults = [ ...quiz_results ];
		if(hash_value.indexOf('/new') == -1) {
			//estem a la landing de qr, no borrem news xk els hem descartat
			to_save_qresults = to_save_qresults.filter((obj) => (obj.QuizResult.id && obj.QuizResult.id != 'new'));
		}

		$.post(`/quizs/saveresults/${promotion.id}`, {
			results:JSON.stringify(to_save_qresults),
		}, (res) => {
			state_promotion.quiz_results = res.qr;
			setQuizResults(res.qr);
			if(cb != undefined) cb();
		}, 'JSON');
	};

	const reset = () => {
		setFoo(getSeed());
		setUser(false);
		setFinal(false);
	};

	const inmutableChange = (q, a, v) => {
		const d = [];
		debugger;
		const _questions = [ ...questions ];
		for(let i in questions) {
			if(_questions[i].Question.id != q) {
				d.push(_questions[i]);
				continue;
			}
			
			if(a == undefined) {
				d.push(v);
				continue;
			}
			
			for(let j in _questions[i].Answer) {
				if(_questions[i].Answer[j].id != a) continue;
				_questions[i].Answer[j] = v;
			}

			d.push(_questions[i]);
		}

		setQuestions(d);
	};

	const quizFinished = (score, user) => {
		setFinal(true);
	};

	const changeLayout = (scope, k, v) => {
		if(props.stage.layout_props == undefined) props.stage.layout_props = {};
		if(props.stage.layout_props[scope] == undefined) props.stage.layout_props[scope] = {};

		if(v != undefined && v != '') {
			props.stage.layout_props[scope] = { ...props.stage.layout_props[scope], [k]:v };
		} else if(props.stage.layout_props[scope][k] != undefined) {
			delete props.stage.layout_props[scope][k];

			if(Object.keys(props.stage.layout_props[scope]).length == 0) {
				delete props.stage.layout_props[scope];
			}

			props.stage.layout_props = { ...props.stage.layout_props };
		}

		layout_props_model.quiz = getLayoutQuiz(props.stage, layout_props_model);
		props.changeStage('layout_props', props.stage.layout_props);

		changeCssVariable(layout_props_model);
	};

	const gotoQuestionEditor = (id) => {
		window.location.hash = `${base}questions/${id}`;
	};

	const gotoFinalMessagesEditor = (id) => {
		window.location.hash = `${base}results/${id}`;
	};

	const is_multiparticipation = promotion?.multi_participation ?? false;
	const is_multistage = promotion?.is_multistage ?? false;

	if(questions.length == 0) {
		return (
			<BuilderMainWithoutSidebar pm={state_promotion}>
				<section class="not_awesome">
					<div class="gui_section_header alone_section_header cleared_header">
						<SectionTitle _class={{ first:true }}>Do you want to add a Quiz to your promotion?</SectionTitle>

						<ButtonCollection>
							<CreateButton
								onClick={() => setNewQuestion(true)}
							>Create your first question to begin</CreateButton>
						</ButtonCollection>
					</div>

					<SectionSubtitle>With the quiz questions you can create quizzes, multiple-choice tests, surveys and predictions in just a few minutes.</SectionSubtitle>

					<div class="gui_not_in_top_big">
						<strong class="block_title">{i18n("With the quiz questions you can")}:</strong>

						<ul class="bulleted_list">
							<li>{i18n("Create one or more questions.")}</li>
							<li>{i18n("Choose between different question types: Single answers, Multiple-answer, sort options, dropdown, auto-complete lists, and rating options.")}</li>
							<li>{i18n("Assign points and values of the answers and establish final messages according to the user's answers.")}</li>
							<li>{i18n("Show the questions and answers with images and videos.")}</li>
							<li>{i18n("Show, optionally, a summary of the user's answers.")}</li>
							<li>{i18n("Show feedback messages according to the user's answers.")}</li>
							<li>{i18n("Test the flow of the quiz before publishing.")}</li>
						</ul>
					</div>
				</section>

				{(new_question) && (
					<CreateNewQuestionLayer
						selectType={(t) => addNewQuestion(t, props.history)}
						closeLayer={() => setNewQuestion(false)}
						promotion={promotion}
						intl={props.intl}
					/>
				)}
			</BuilderMainWithoutSidebar>
		);
	}

	const qr_model = (props.stage_id != undefined) ? props.stage : promotion;

	let counter_final_messages = 0;

	const navigation_tabs_props = {
		pm:promotion,
		basename:props.basename,
		is_multistage,
		stage:props.stage,
		stage_indicator:props.stage_indicator,
		intl:props.intl,
	};
	
	const default_field_props = { size:50 };

	return (
		<Provider store={props.store}>
			<Router hashType="noslash" basename={props?.basename ?? ''}>
				<>
					<Route exact={true} path="/questions" render={(route) => {
						let question_number = 0;

						const question_cards = [];
						
						questions.map((q) => {
							const _question = q.Question;
							const is_active = (_question.status) ? true : false;

							let question_type = "";
							switch(_question.type) {
								case '1':
								default:
									question_type = "single";
									break;

								case '2':
									question_type = "multi";
									break;

								case '3':
									question_type = "sortable";
									break;

								case '4':
									question_type = "advanced";
									break;
							}

							if(is_active) question_number++;

							const answer_ribbons = [];
							q.Answer.map((a) => {
								answer_ribbons.push({
									title:a.answer,
									autotranslate:false,
									color:'lightgray',
								});
							})

							question_cards.push({
								id:_question.id,
								initials:(is_active) ? question_number : "--",
								title:(_question.question != "") ? stripTags(_question?.question ?? '') : `[ ${i18n("Untitled question")} ]`,
								full_image:isNotEmpty(_question, 'fullimage') ? _question.fullimage : null,
								role:<span class="blackBg whiteColor">{i18n(question_type)}</span>,
								ribbons:[answer_ribbons],
								self:false,
							});
						});

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={saveQuiz}>
									<Nav
										{...navigation_tabs_props}
										active={route.location.pathname}
									/>

									<article>
										<BuilderSubSectionTitle
											title="Quiz"
											bordered={false}
											title_actions={<CreateButton
												icon={false}
												onClick={() => setNewQuestion(true)}
												data-mouse_event='{ "right":"copyItem-contextMenu" }'
												data-mouse-key="{}"
											>New question</CreateButton>}
										/>

										<SortableQuestions
											basename={props.basename}
											items={questions}
											actions={{
												toggleQuestion,
												copyQuestion,
												deleteQuestion,
											}}
											onSortStart={() => preventSelection(true)}
											onSortEnd={onSortEnd}
											useDragHandle={true}
											intl={props.intl}
										/>
									</article>
								</BuilderSidebar>

								<BuilderCanvas
									is_adaptable={false}
									fit_to_window="content"
									not_awesome={true}
									add_public_css_vars={false}
								>
									<div style={{ padding:'1em', height:'calc(100dvh - 121px)', overflow:'auto' }}>
										<AgentCards
											items={question_cards}
											whole_action={gotoQuestionEditor}
											num_cols='4'
										/>
									</div>
								</BuilderCanvas>

								{(new_question) && (
									<CreateNewQuestionLayer
										selectType={(t) => addNewQuestion(t, route.history)}
										closeLayer={() => setNewQuestion(false)}
										promotion={promotion}
										intl={props.intl}
									/>
								)}
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/design" render={(route) => {
						const model_question = layout_props_model.quiz.question;
						const model_answer = layout_props_model.quiz.answer;
						const model_continue = layout_props_model.quiz.continue;

						const font_weights = [
							{ k:'light', v:"***Light" },
							{ k:'roman', v:"Roman" },
							{ k:'bold', v:"Bold" },
						];
					
						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={props.saveStage}>
									<Nav
										{...navigation_tabs_props}
										active={route.location.pathname}
									/>

									<article>
										<BuilderSubSectionTitle
											title="Questions"
											icon='knk-questions'
										/>

										<Model
											model={model_question}
											change={(k, v) => changeLayout('question', k, v)}
										>
											<Checkbox
												name="force_align_center"
												value={ensureBoolean(model_question?.force_align_center ?? false)}
											>Center question text</Checkbox>
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Answers"
											icon='knk-button'
										/>

										<Model
											model={model_answer}
											change={(k, v) => changeLayout('answer', k, v)}
										>
											<Select
												name='font_weight'
												values={font_weights}
												_class={default_field_props}
											>Font weight</Select>

											<Checkbox
												name="force_uppercase"
												value={ensureBoolean(model_answer?.force_uppercase ?? false)}
											>Force uppercase</Checkbox>

											<ColorPicker
												name="color"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.color}
											>Text color</ColorPicker>

											<ColorPicker
												name="background_color"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.background_color}
											>Background color</ColorPicker>

											<Range
												min={0}
												max={5}
												name="border_width"
												default={1}
												step={1}
												_class={default_field_props}
												label_props={{
													_extra_component:i18n("in 'px'"),
												}}
											>Border width</Range>

											{(parseInt(model_answer.border_width) > 0) && (
												<ColorPicker
													name="border_color"
													_class={default_field_props}
													_hide_code={true}
													color={model_answer.border_color}
												>Border color</ColorPicker>
											)}
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Answers (hover)"
											icon='knk-button'
										/>

										<Model
											model={model_answer}
											change={(k, v) => changeLayout('answer', k, v)}
										>
											<ColorPicker
												name="color_hover"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.color_hover}
											>Text color</ColorPicker>

											<ColorPicker
												name="background_color_hover"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.background_color_hover}
											>Background color</ColorPicker>

											{(parseInt(model_answer.border_width) > 0) && (
												<ColorPicker
													name="border_color_hover"
													_class={default_field_props}
													_hide_code={true}
													color={model_answer.border_color}
												>Border color</ColorPicker>
											)}
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Selected answers"
											icon='knk-button'
										/>

										<Model
											model={model_answer}
											change={(k, v) => changeLayout('answer', k, v)}
										>
											<ColorPicker
												name="tick_color"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.tick_color}
											>Tick color</ColorPicker>

											<ColorPicker
												name="tick_bg_color"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.tick_bg_color}
											>Tick background color</ColorPicker>

											<ColorPicker
												name="color_selected"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.color_selected}
											>Text color</ColorPicker>

											<ColorPicker
												name="background_color_selected"
												_class={default_field_props}
												_hide_code={true}
												color={model_answer.background_color_selected}
											>Background color</ColorPicker>

											{(parseInt(model_answer.border_width) > 0) && (
												<ColorPicker
													name="border_color_selected"
													_class={default_field_props}
													_hide_code={true}
													color={model_answer.border_color_selected}
												>Border color</ColorPicker>
											)}
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Continue button"
											icon='knk-button'
										/>

										<Model
											model={model_continue}
											change={(k, v) => changeLayout('continue', k, v)}
										>
											<Checkbox
												name="override_styles"
												value={ensureBoolean(model_continue?.override_styles ?? false)}
											>Override default button styles</Checkbox>
										</Model>

										{(model_continue?.override_styles ?? false) && (
											<Model
												model={model_continue}
												change={(k, v) => changeLayout('continue', k, v)}
											>
												<ColorPicker
													name="color"
													_class={default_field_props}
													_hide_code={true}
													color={model_continue.color}
												>Text color</ColorPicker>

												<ColorPicker
													name="background_color"
													_class={default_field_props}
													_hide_code={true}
													color={model_continue.background_color}
												>Background color</ColorPicker>

												<ColorPicker
													name="color_hover"
													_class={default_field_props}
													_hide_code={true}
													color={model_continue.color_hover}
												>Text color (hover)</ColorPicker>

												<ColorPicker
													name="background_color_hover"
													_class={default_field_props}
													_hide_code={true}
													color={model_continue.background_color_hover}
												>Background color (hover)</ColorPicker>
											</Model>
										)}
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Options"
											icon='knk-settings'
										/>

										<DisabledArea
											disabled={(questions.length < 2) ? true : false}
											message="You need at least 2 questions"
										>
											<Select
												name="quiz_progress_type"
												values={[
													{ k:'pagination', v:i18n("Number of questions") },
													{ k:'bar', v:i18n("Progress bar") },
													{ k:'none', v:i18n("No pagination") },
												]}
												model={qr_model}
												change={(k, v) => { changeEntity(k, v); saveEntity(); }}
											>How do you want to show the progress?</Select>

											<Checkbox
												name="quiz_enable_change"
												value={qr_model?.quiz_enable_change ?? true}
												model={qr_model}
												change={(k, v) => { changeEntity(k, v); saveEntity(); }}
											>Allow users to change their answers</Checkbox>
										</DisabledArea>
									</article>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									<Layout
										promo={promotion}
										menus={state_promotion.Custom__Menus}
										is_builder={true}
										page="quiz"
									>
										<Question
											id={questions[0].Question.id}
											promo={promotion}
											is_builder={true}
											fake_selected_answer={true}
											is_game={false}
											isRight={true}
											multiparticipation={is_multiparticipation}
											stage={props.stage}
										/>
									</Layout>
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/questions_container" render={(route) => {
						const avail_transition_axis = [
							{ k:'h', v:`${i18n("Horizontal")} (${i18n("default")})` },
							{ k:'v', v:i18n("Vertical") },
						];

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={savePromo}>
									<Nav
										{...navigation_tabs_props}
										active={route.location.pathname}
									/>

									<article>
										<Model model={promotion} change={change}>
											<Select
												_class={{first:true}}
												name="config.transition_axis"
												values={avail_transition_axis}
											>Direction of transition from one question to the next</Select>

											<Range
												min={40}
												max={100}
												value={parseInt(promotion.config.layout.quiz.width)}
												name="config.layout.quiz.width"
												default={60}
												step={10}
												_class={{ size:50 }}
											>Container width</Range>
										</Model>
									</article>
								</BuilderSidebar>

								<DesignContainerProps
									promo={promotion}
								/>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/quizstats" render={(route) => {
						if(is_multistage) {
							window.location.hash = `${props.basename}/questions`;
							return null;
						}

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={savePromo}>
									<Nav
										{...navigation_tabs_props}
										active={route.location.pathname}
									/>

									<article>
										<Model model={promotion} change={change}>
											<Checkbox
												name="show_quiz_question_stats"
												label_props={{
													tooltip:<Tooltip>A button will be displayed on the Thank you page, after participating. By clicking on this button, an overlayed window will show up with the quiz stats.</Tooltip>,
												}}
												_class={{ first:true }}
											>Make stats public</Checkbox>

											{(promotion.show_quiz_question_stats) && (
												<Input
													name="quiz_stats_link"
												>Label of the button that opens the stats</Input>
											)}
										</Model>
									</article>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={false}>
									<QuizStats
										promotion_id={promotion.id}
									/>
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/questions/:id" render={(route) => {
						const full_question = findQuestionById(route.match.params.id);
						const question = full_question?.Question ?? {};

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={saveQuiz}>
									<Nav
										{...navigation_tabs_props}
										active="/questions"
										qid={route.match.params.id}
									/>

									<QuestionOptions
										basename={props.basename}
										question={full_question}
										saveQuiz={saveQuiz}
										dispatch={props.store.dispatch}
										onChange={inmutableChange}
										is_game={false}
										is_multiparticipation={is_multiparticipation}
										stage={props.stage}
									/>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									<Layout
										promo={promotion}
										menus={state_promotion.Custom__Menus}
										is_builder={true}
										page="quiz"
									>
										<Question
											id={question.id}
											promo={promotion}
											is_builder={true}
											is_game={false}
											isRight={true}
											multiparticipation={is_multiparticipation}
											stage={props.stage}
										/>
									</Layout>
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/questions/:id/:answer_id" render={(route) => {
						const full_question = findQuestionById(route.match.params.id);
						const question = full_question.Question;

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={saveQuiz}>
									<Nav
										{...navigation_tabs_props}
										active={route.location.pathname}
									/>

									<QuestionOptions
										basename={props.basename}
										question={full_question}
										saveQuiz={saveQuiz}
										dispatch={props.store.dispatch}
										currentAnswer={route.match.params.answer_id}
										onChange={inmutableChange}
										onlyAnswer={true}
										is_multiparticipation={is_multiparticipation}
									/>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									<Layout
										promo={promotion}
										menus={state_promotion.Custom__Menus}
										is_builder={true}
										page="quiz"
									>
										<Question
											id={question.id}
											multiparticipation={is_multiparticipation}
										/>
									</Layout>
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/results" render={(route) => {
						const quiz_score_type = [
							{ k:'0', v:"By summing all points" },
							{ k:'1', v:"By majority of answers" },
						];

						const saved_qr = quiz_results.filter((obj) => (obj.QuizResult.id && obj.QuizResult.id != 'new'));

						// if(saved_qr.length == 0) {
						// 	const link_zendesk_final_messages_tutorial = `${window.zendesk_base_url}207886456`;

						// 	return (
						// 		<BuilderMainWithoutSidebar pm={state_promotion} has_tabs={true}>
						// 			<Nav
						// 				{...navigation_tabs_props}
						// 				active="/results"
						// 			/>

						// 			<section class="not_awesome">
						// 				<div>
						// 					<div class="gui_section_header alone_section_header cleared_header">
						// 						<SectionTitle _class={{ other:'in_top' }}>Do you want to show a customized final message to users?</SectionTitle>

						// 						<ButtonCollection>
						// 							<CreateButton
						// 								onClick={addNewQr}
						// 							>Create new final message now</CreateButton>
						// 						</ButtonCollection>
						// 					</div>

						// 					<SectionSubtitle>From this section you can create a personalized final message based on the user's answers in the quiz.</SectionSubtitle>

						// 					<SectionSubtitle>You can complete final messages with photos, videos and external links, and they will be shown to the user after completing the quiz.</SectionSubtitle>

						// 					<SectionSubtitle autotranslate={false}><ActionLink _href={link_zendesk_final_messages_tutorial} _target="_blank">Learn how to create customized final messages</ActionLink></SectionSubtitle>
						// 				</div>

						// 				<article class="gui_not_in_top_huge">
						// 					<div class="gui_margined_children_big_v">
						// 						<div>
						// 							<strong class="block_title">{i18n("You can use final messages to")}:</strong>

						// 							<div>
						// 								<ul class="bulleted_list">
						// 									<li>{i18n("Show each user a personalized final message according to their answers.")}</li>

						// 									<li>{i18n("Inform each user of their final score in the quiz.")}</li>

						// 									<li>{i18n("Allow users to share their quiz result via social media.")}</li>

						// 									<li>{i18n("Suggest each user the product of your brand that best fits according to their answers.")}</li>

						// 									<li>{i18n("Send an automatic email to each user with the final message so that they can save it.")}</li>
						// 								</ul>
						// 							</div>
						// 						</div>
						// 					</div>
						// 				</article>
						// 			</section>
						// 		</BuilderMainWithoutSidebar>
						// 	)
						// }

						const stage_type = parseInt(props?.stage?.promotion_type_id ?? '1');

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={saveResults}>
									<Nav
										{...navigation_tabs_props}
										active="/results"
									/>

									<article>
										<Model
											model={qr_model}
											change={(k, v) => { changeEntity(k, v); saveEntity(); }}
										>
											{([spt.PERSONALITY_QUIZ.id, spt.PRODUCT_RECOMMENDER.id].includes(stage_type)) && (
												<Select
													name="quiz_score_type"
													values={quiz_score_type}
													_class={{ first:true }}
													label_props={{
														tooltip:<Tooltip>Choose how the user's answers will be evaluated to display the final message: ''By summing all points'' sums the score from all answers, while ''By majority of answers'' uses the most frequently selected answer to determine the result.</Tooltip>
													}}
												>Select the scoring system</Select>
											)}

											{([spt.KNOWLEDGE_QUIZ.id].includes(stage_type)) && (
												<Input
													name="completed_score"
													value={props.stage?.completed_score ?? ''}
													_class={{ first:true }}
													label_props={{
														tooltip:<Tooltip>Set the minimum number of points a user must score for their quiz to be considered completed.</Tooltip>
													}}
												>Minimum score to complete the quiz</Input>
											)}
										</Model>
									</article>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Final messages"
											bordered={(quiz_results.length > 0) ? false : null}
											title_actions={<CreateButton
												icon={false}
												onClick={addNewQr}
											>New final message</CreateButton>}
										/>

										{(saved_qr.length > 0) && (
											<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap">
												{quiz_results.map((qr, i) => {
													let range = (qr.QuizResult.min == qr.QuizResult.max) ? qr.QuizResult.min : qr.QuizResult.min+' - '+qr.QuizResult.max;
													range = (range != null && range != '') ? '[' + range + '] ' : '';

													if(qr.QuizResult.id == 'new') return null;
													if(props.stage_id != undefined && props.stage_id != qr.QuizResult.stage) return null;

													return (
														<li key={i} class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
															<div class="item_header">
																<div class="item_title_container">
																	<div>
																		<ActionLink
																			_class={{ other:'item_title without_icon' }}
																			_href={`${base}results/${qr.QuizResult.id}`}
																			autotranslate={false}
																		>{range}<strong>{qr.QuizResult.message_st}</strong></ActionLink>
																	</div>
																</div>

																<ButtonCollection>
																	<SquaredButton
																		icon='knk-edit'
																		_href={`${base}results/${qr.QuizResult.id}`}
																		aria-label="Edit"
																	/>

																	<SquaredButton
																		icon='knk-copy'
																		onClick={() => copyResult(qr.QuizResult)}
																		aria-label="Copy"
																	/>

																	<DeleteButton
																		squared={true}
																		_position="left"
																		onClick={() => deleteResult(qr.QuizResult)}
																		aria-label="Delete"
																	/>
																</ButtonCollection>
															</div>
														</li>
													)
												})}
											</ul>
										)}

										{(saved_qr.length == 0) && (
											<p class="gui_not_in_top_tiny">{i18n("You haven't defined any final message yet.")}</p>
										)}
									</article>
								</BuilderSidebar>

								<BuilderCanvas
									is_adaptable={false}
									fit_to_window="content"
									not_awesome={true}
									add_public_css_vars={false}
								>
									<div style={{ padding:'1em', height:'calc(100dvh - 121px)', overflow:'auto' }}>
										{(saved_qr.length > 0) && (
											<div class="builder_multilist_preview cards_display">
												<div class="container" style={{ gridTemplateColumns:'repeat(4, 1fr)' }}>
													{saved_qr.map((qr, i) => {
														if(props.stage_id != undefined && props.stage_id != qr.QuizResult.stage) return null;
														counter_final_messages++;

														return (
															<div
																key={`final_${i}`}
																class="_card"
																onClick={() => gotoFinalMessagesEditor(qr.QuizResult.id)}
															>
																<div class="gui_pos_rel avatar_manager">
																	<span class="account_initials gui_pos_abs">{(counter_final_messages)}</span>

																	{(isNotEmpty(qr.QuizResult, 'fullimage')) && (
																		<span
																			style={{
																				backgroundImage:`url(${qr.QuizResult.fullimage})`,
																			}}
																		><img src={`${window.cdn_url}img/blank_1x1.png`} width="100%" /></span>
																	)}

																	<p class="role">
																		<span class="blackBg whiteColor">{(qr.QuizResult.points_range != "") ? qr.QuizResult.points_range : i18n("All")}</span>
																	</p>
																</div>

																<div class="_info">
																	<h4 class="title">{(qr.QuizResult.message_st != "") ? qr.QuizResult.message_st : `[ ${i18n("Untitled question")} ]`}</h4>
																</div>
															</div>
														)
													})}
												</div>
											</div>
										)}

										{(saved_qr.length == 0) && (
											<>
												<p>{i18n("In this section, you can customize different final messages based on the score the user has obtained in the quiz.")}</p>

												<p class="gui_not_in_top_small knk-tip">
													{i18n("Help")}: <ActionLink
														_href={`${window.zendesk_base_url}207886456`}
														_target='_blank'
													>How the quiz scoring and final messages system works</ActionLink>.
												</p>
											</>
										)}
									</div>
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/results/:id" render={(route) => {
						const qr_id = route.match.params.id;

						const qr = quiz_results.find((q) => (q.QuizResult.id == qr_id));
						if(qr == undefined) {
							window.location.hash = `${props?.basename ?? ""}/results`;
							return null;
						}

						const has_ranking = promotion?.has_ranking ?? false;
						const ViewComponent = (!has_ranking) ? SuccessFlex : QuizResultFlex;

						const view_props = {
							is_builder:true,
						};
						if(!has_ranking) {
							view_props.promotion = { Promotion:promotion };
							view_props.quiz_message = qr.QuizResult;
							view_props.user = { User:{} };
						} else {
							view_props.promotion = promotion;
							view_props.response = { qr:qr.QuizResult };
							if(qr.QuizResult.share_title != "") view_props.response.share = {};
						}

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar onSave={saveResults}>
									<Nav
										{...navigation_tabs_props}
										active="/results"
									/>

									<article class="side_section">
										<QuizResultOptions
											base={base}
											is_multiparticipation={is_multiparticipation}
											pm={promotion}
											share_options={promotion?.config?.share ?? { status:'1', networks:{}}}
											qr={qr}
											results={quiz_results}
											onChange={(k, v) => quizResultChange(k, v, qr_id)}
										/>
									</article>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									<Layout
										promo={promotion}
										menus={state_promotion.Custom__Menus}
										page="success"
										without_context={false}
										is_builder={true}
									>
										<ViewComponent
											{...view_props}
										/>
									</Layout>
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					<Route exact={true} path="/testreact" render={(route) => {
						const score_type = props?.stage?.quiz_score_type ?? promotion.quiz_score_type;

						const score = getQuizScore(questions, user, (score_type == '1') ? 'majority' : 'sum');

						const userSorted = [];
						for(let i in questions) {
							if(user[questions[i].Question.id] == undefined) continue;
							userSorted.push(questions[i]);
						}

						const progress_type = props?.stage?.quiz_progress_type ?? 'pagination';	// 'pagination' | 'bar'

						return (
							<BuilderMain pm={state_promotion}>
								<BuilderSidebar
									display_bottom_buttons={false}
									onSave={savePromo}
								>
									<Nav
										{...navigation_tabs_props}
										active={route.location.pathname}
									/>

									<article>
										<BuilderSubSectionTitle
											title="Test debug tool"
											title_actions={<CreateButton
												icon={false}
												onClick={() => reset()}
											>Reset test</CreateButton>}
											subtitle="Use the test tool to check the general performance of your quiz and its final design: scoring, feedback messages, color customization and final messages."
										/>

										<Message>
											{(score_type == "1") ? (
												<p>{i18n("Score")} {i18n("By majority of answers", 'lowercase')}: <strong>{getMostAnswer(score)}</strong></p>
											) : (
												<p>{i18n("Score")} {i18n("By summing all points", 'lowercase')}: <strong>{score}</strong></p>
											)}
										</Message>

										{(user) && (
											<UserTestResumee
												user={user}
												questions={userSorted}
												score_type={score_type}
												intl={props.intl}
											/>
										)}
									</article>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									{(final) && (
										<Layout
											promo={promotion}
											menus={state_promotion.Custom__Menus}
											page="success"
											without_context={false}
											is_builder={true}
										>
											<SuccessFlex
												promotion={promotion}
												score={score}
												quiz_message={getQuizResult(score_type == 1 ? getMostAnswer(score) : score, quiz_results)}
												user={{ User:{} }}
												without_context={false}
												is_builder={true}
												stage={(props.stage) ? { Stage:props.stage } : null}
											/>
										</Layout>
									)}
									
									{(!final) && (
										<Layout
											promo={promotion}
											menus={state_promotion.Custom__Menus}
											is_builder={true}
											page="quiz"
										>
											<div class={'_quiz_container' + ((progress_type == 'pagination' && questions.length > 1) ? ' with_paginator' : '')}>
												<QuizClient
													key={foo}
													is_builder={true}
													is_game={false}
													stage={props.stage ? { Stage:props.stage } : null}
													questions={questions.filter((q) => (q.Question.status))}
													store={props.store}
													clientAction={clientAction}
													promotion={{Promotion:promotion}}
													quizFinished={quizFinished}
													progress_type={progress_type}
												/>
											</div>
										</Layout>
									)}
								</BuilderCanvas>
							</BuilderMain>
						)}}
					/>

					{(promotion.promo_type == '2') && (
						<Route exact={true} path="/quiz_advanced" render={(route) => {
							const default_script_placeholder = `<script type="text/javascript">\n\tdocument.addEventListener("DOMContentLoaded", function() {\n\t\t/* ${i18n("Your code goes here")}... */\n\t});\n</script>`;

							return (
								<BuilderMain pm={state_promotion}>
									<BuilderSidebar onSave={saveEntity}>
										<Nav
											{...navigation_tabs_props}
											active={route.location.pathname}
										/>

										<article>
											<Model
												model={qr_model}
												change={changeEntity}
											>
												<TextAreaPlaceHolder
													_class={{ v_size:'huge', first:true }}
													name="code_tracking_quiz"
													zoomable={true}
													is_monospaced={true}
													placeholder={default_script_placeholder}
													label_props={{
														tooltip:<Tooltip>In this field you can insert HTML, Javascript or any other code that can be read by a web browser.</Tooltip>,
													}}
												>Javascript, HTML and your own code</TextAreaPlaceHolder>
											</Model>
										</article>
									</BuilderSidebar>

									<DesignWarningMessage
										promotion={promotion}
									/>
								</BuilderMain>
							)}}
						/>
					)}
				</>
			</Router>
		</Provider>
	)
};

export default injectIntl(Quiz);